import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Mail, Users, FileText, MoreHorizontal, Globe, Settings, Gift, Headphones, BookOpen, FormInput, List, Megaphone, LucideIcon } from "lucide-react";
import { cn } from '@/shared/utils/cn';
import { useIsMobile } from '@/shared/hooks';
import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Separator } from "@/shared/components/ui/separator";
import { isFeatureEnabled, FeatureFlag } from '@/core/config/featureFlags';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  group?: string;
  featureFlag?: FeatureFlag;
}

const primaryNavItems: NavItem[] = [
  { icon: Home, label: "Dashboard", path: "/" },
  { icon: Megaphone, label: "Campaigns", path: "/campaigns" },
  { icon: Users, label: "Contacts", path: "/contacts" },
  { icon: Mail, label: "Mail", path: "/mail", featureFlag: "mail_library" },
];

const secondaryNavItems: NavItem[] = [
  { icon: Globe, label: "Landing Pages", path: "/landing-pages", group: "Campaigns", featureFlag: "landing_pages" },
  { icon: FormInput, label: "Forms", path: "/forms", group: "Campaigns" },
  { icon: List, label: "Lists & Segments", path: "/contacts/lists", group: "Audience" },
  { icon: Gift, label: "Gift Cards", path: "/settings/gift-card-inventory", group: "Settings" },
  { icon: Headphones, label: "Call Center", path: "/call-center", group: "Operations" },
  { icon: BookOpen, label: "Help", path: "/docs", group: "Settings" },
  { icon: Settings, label: "Settings", path: "/settings", group: "Settings" },
];

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Filter primary nav items based on feature flags
  const visiblePrimaryItems = useMemo(() => 
    primaryNavItems.filter(item => !item.featureFlag || isFeatureEnabled(item.featureFlag)),
    []
  );

  // Filter secondary nav items based on feature flags and group them
  const groupedSecondaryItems = useMemo(() => {
    return secondaryNavItems
      .filter(item => !item.featureFlag || isFeatureEnabled(item.featureFlag))
      .reduce((acc, item) => {
        const group = item.group || "Other";
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
      }, {} as Record<string, NavItem[]>);
  }, []);

  if (!isMobile) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white dark:bg-[rgba(6,11,24,0.95)] border-t border-border">
      <div className="flex items-center justify-around px-2 py-2 safe-area-inset-bottom">
        {visiblePrimaryItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                "relative flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-[10px] transition-all duration-200 min-h-[56px] min-w-[64px]",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground active:scale-95"
              )}
            >
              <Icon className={cn("h-5 w-5 transition-all", active && "scale-105")} />
              <span className="text-xs font-medium">{item.label}</span>
              {active && (
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-[3px] rounded-full bg-primary" />
              )}
            </button>
          );
        })}
        
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              className="flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-[var(--radius)] transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-card/60 active:scale-95 min-h-[56px] min-w-[64px]"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span className="text-xs font-medium">More</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[70vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>More Navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Additional navigation options
              </SheetDescription>
            </SheetHeader>
            
            <div className="mt-6 space-y-6">
              {Object.entries(groupedSecondaryItems).map(([groupName, items]) => (
                <div key={groupName}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">
                    {groupName}
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      
                      return (
                        <Button
                          key={item.path}
                          variant={active ? "default" : "outline"}
                          className="h-20 flex-col gap-2"
                          onClick={() => {
                            navigate(item.path);
                            setSheetOpen(false);
                          }}
                        >
                          <Icon className="h-6 w-6" />
                          <span className="text-xs text-center">{item.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                  <Separator className="mt-6" />
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
